import { createSlice } from "@reduxjs/toolkit";
import { getAllRoles, createNewRole, updateRole, deleteRole, toggleRoleStatus, getAllRolesSummary, getRolesDropdown, getRoleById, } from "../thunk/role-thunk";
const initialState = {
    roles: null,
    paginationInfo: null,
    selectedRole: null,
    loading: false,
    error: null,
    createLoading: false,
    createError: null,
    updateLoading: false,
    updateError: null,
    deleteLoading: false,
    deleteError: null,
    toggleStatusLoading: false,
    toggleStatusError: null,
    selectedRoleRow: null,
    roleFilters: {
        name: undefined,
        isActive: null,
        isSystemRole: null,
        globalSearch: undefined,
    },
    currentPageNumber: 1,
    currentPageSize: 25,
    rolesSummary: null,
    summaryLoading: false,
    summaryError: null,
    rolesDropdown: null,
    dropdownLoading: false,
    dropdownError: null,
    getRoleByIdLoading: false,
    getRoleByIdError: null,
};
const rolesSlice = createSlice({
    name: "roles",
    initialState,
    reducers: {
        setSelectedRole: (state, action) => {
            state.selectedRole = action.payload;
        },
        clearError: (state) => {
            state.error = null;
            state.createError = null;
            state.updateError = null;
            state.deleteError = null;
            state.toggleStatusError = null;
        },
        setSelectedRoleRow: (state, action) => {
            state.selectedRoleRow = action.payload;
        },
        resetSelectedRoleRow: (state) => {
            state.selectedRoleRow = null;
        },
        clearRoles: (state) => {
            state.roles = null;
            state.paginationInfo = null;
            state.selectedRole = null;
        },
        updateFilters: (state, action) => {
            // Check if filters are actually changing
            const oldFilters = state.roleFilters;
            const newFilters = Object.assign(Object.assign({}, state.roleFilters), action.payload);
            // Compare if anything actually changed
            let hasChanged = false;
            for (const key in action.payload) {
                if (JSON.stringify(oldFilters[key]) !==
                    JSON.stringify(newFilters[key])) {
                    hasChanged = true;
                    break;
                }
            }
            state.roleFilters = newFilters;
            // Only reset to first page if filters actually changed
            if (hasChanged) {
                state.currentPageNumber = 1;
            }
        },
        setPagination: (state, action) => {
            state.currentPageNumber = action.payload.pageNumber;
            state.currentPageSize = action.payload.pageSize;
        },
        setPageNumber: (state, action) => {
            state.currentPageNumber = action.payload;
        },
        setPageSize: (state, action) => {
            state.currentPageSize = action.payload;
        },
        setFiltersFromForm: (state, action) => {
            state.roleFilters = action.payload;
        },
        setGlobalSearch: (state, action) => {
            state.roleFilters.globalSearch = action.payload;
            state.currentPageNumber = 1;
        },
        resetFilters: (state) => {
            state.roleFilters = {
                name: undefined,
                isActive: undefined,
                isSystemRole: undefined,
                globalSearch: undefined,
            };
            state.currentPageNumber = 1;
        },
        resetFiltersAndPagination: (state) => {
            state.roleFilters = {
                name: undefined,
                isActive: null,
                isSystemRole: null,
                globalSearch: undefined,
            };
            state.currentPageNumber = 1;
            state.currentPageSize = 25;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get All Roles
            .addCase(getAllRoles.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(getAllRoles.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            if (action.payload.data) {
                state.roles = action.payload.data.items;
                state.paginationInfo = {
                    totalCount: action.payload.data.totalCount,
                    pageNumber: action.payload.data.pageNumber,
                    pageSize: action.payload.data.pageSize,
                    totalPages: action.payload.data.totalPages,
                    hasPreviousPage: action.payload.data.hasPreviousPage,
                    hasNextPage: action.payload.data.hasNextPage,
                };
            }
        })
            .addCase(getAllRoles.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.roles = null;
            state.paginationInfo = null;
        })
            // Create New Role
            .addCase(createNewRole.pending, (state) => {
            state.createLoading = true;
            state.createError = null;
        })
            .addCase(createNewRole.fulfilled, (state, action) => {
            state.createLoading = false;
            state.createError = null;
            if (state.roles) {
                state.roles.push(action.payload.data);
            }
        })
            .addCase(createNewRole.rejected, (state, action) => {
            state.createLoading = false;
            state.createError = action.payload;
        })
            // Update Role
            .addCase(updateRole.pending, (state) => {
            state.updateLoading = true;
            state.updateError = null;
        })
            .addCase(updateRole.fulfilled, (state, action) => {
            state.updateLoading = false;
            state.updateError = null;
            if (state.roles) {
                const index = state.roles.findIndex((role) => { var _a; return role.id === ((_a = action.payload.data) === null || _a === void 0 ? void 0 : _a.id); });
                if (index !== -1 && action.payload.data) {
                    state.roles[index] = action.payload.data;
                }
            }
            // Keep selectedRole in sync so the edit page shows fresh permissions
            if (action.payload.data) {
                state.selectedRole = action.payload.data;
            }
        })
            .addCase(updateRole.rejected, (state, action) => {
            state.updateLoading = false;
            state.updateError = action.payload;
        })
            // Delete Role
            .addCase(deleteRole.pending, (state) => {
            state.deleteLoading = true;
            state.deleteError = null;
        })
            .addCase(deleteRole.fulfilled, (state, action) => {
            state.deleteLoading = false;
            state.deleteError = null;
            if (state.roles && action.payload.id) {
                state.roles = state.roles.filter((role) => role.id !== action.payload.id);
            }
        })
            .addCase(deleteRole.rejected, (state, action) => {
            state.deleteLoading = false;
            state.deleteError = action.payload;
        })
            // Toggle Role Status
            .addCase(toggleRoleStatus.pending, (state) => {
            state.toggleStatusLoading = true;
            state.toggleStatusError = null;
        })
            .addCase(toggleRoleStatus.fulfilled, (state, action) => {
            state.toggleStatusLoading = false;
            state.toggleStatusError = null;
            if (state.roles &&
                action.payload.id !== undefined &&
                action.payload.isActive !== undefined) {
                const roleIndex = state.roles.findIndex((role) => role.id === action.payload.id);
                if (roleIndex !== -1) {
                    state.roles[roleIndex].isActive = action.payload.isActive;
                }
            }
        })
            .addCase(toggleRoleStatus.rejected, (state, action) => {
            state.toggleStatusLoading = false;
            state.toggleStatusError = action.payload;
        })
            // Get All Roles Summary
            .addCase(getAllRolesSummary.pending, (state) => {
            state.summaryLoading = true;
            state.summaryError = null;
        })
            .addCase(getAllRolesSummary.fulfilled, (state, action) => {
            state.summaryLoading = false;
            state.summaryError = null;
            if (action.payload.data) {
                state.rolesSummary = action.payload.data;
            }
        })
            .addCase(getAllRolesSummary.rejected, (state, action) => {
            state.summaryLoading = false;
            state.summaryError = action.payload;
            state.rolesSummary = null;
        })
            // Get roles dropdown
            .addCase(getRolesDropdown.pending, (state) => {
            state.dropdownLoading = true;
            state.dropdownError = null;
        })
            .addCase(getRolesDropdown.fulfilled, (state, action) => {
            state.dropdownLoading = false;
            state.rolesDropdown = action.payload;
        })
            .addCase(getRolesDropdown.rejected, (state, action) => {
            state.dropdownLoading = false;
            state.dropdownError = action.payload;
            state.rolesDropdown = null;
        })
            // Get role by ID
            .addCase(getRoleById.pending, (state) => {
            state.getRoleByIdLoading = true;
            state.getRoleByIdError = null;
        })
            .addCase(getRoleById.fulfilled, (state, action) => {
            state.getRoleByIdLoading = false;
            state.selectedRole = action.payload;
            // Also add to roles array if not present
            if (state.roles) {
                const existingIndex = state.roles.findIndex((r) => r.id === action.payload.id);
                if (existingIndex === -1) {
                    state.roles.push(action.payload);
                }
                else {
                    state.roles[existingIndex] = action.payload;
                }
            }
            else {
                state.roles = [action.payload];
            }
        })
            .addCase(getRoleById.rejected, (state, action) => {
            state.getRoleByIdLoading = false;
            state.getRoleByIdError = action.payload;
        });
    },
});
export const { setSelectedRole, clearError, clearRoles, updateFilters, setPagination, setPageNumber, setPageSize, setFiltersFromForm, setGlobalSearch, resetFilters, resetFiltersAndPagination, setSelectedRoleRow, resetSelectedRoleRow, } = rolesSlice.actions;
export default rolesSlice.reducer;
